import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useProperty,
  useContentToken,
} from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import pagesData from '../pages.json';

// ============================================================================
// Dynamic sub-navigation from pages.json
// ----------------------------------------------------------------------------
// The sub-nav bar renders one link per page that is actually *set to show* —
// i.e. `visible !== false` and not a build-time template. Links are sorted by
// each page's `position`, so this bar is an exact mirror of the visibility
// toggles in pages.json, the same way the main Header nav is (Header.tsx
// `buildNavLinks`). Flip a page's `visible` flag and it appears/disappears in
// BOTH navs at once — there's no hidden curated list. The row is
// `overflow-x-auto`, so any number of visible pages scrolls horizontally
// rather than wrapping or overlapping.
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  parent?: string;
}

interface NavLink {
  label: string;
  href: string;
}

/**
 * Aliases kept out of the sub-navigation regardless of their `visible` flag.
 * Only `schedule-a-tour` — it's already surfaced as the CTA pill on the right
 * of this bar, so listing it again would duplicate it. Every OTHER page is
 * driven purely by its `visible` flag + `position` in pages.json (legal /
 * utility pages are already `visible: false`, so they stay out without being
 * hardcoded). Home IS included here — unlike the Header, the sub-bar has no
 * logo linking home.
 */
const NAV_EXCLUDED_ALIASES = new Set(['schedule-a-tour']);

/**
 * Build the sub-nav links from pages.json: only pages that are visible, not a
 * template, and not in the exclusion list, sorted by `position`. Pages with a
 * `parent` are flattened to the top level here (the sub-bar is a single
 * horizontal row, so there's no dropdown to nest them under).
 *
 * `hasInteractiveLeasing` gates the one page the static build can DROP: the
 * Interactive Map page is omitted from the build (would 404) when the property
 * has no ApartmentSync config, so we mirror that here the same way the Header
 * does. Every other page shows purely on its `visible` flag.
 */
function buildNavLinks(hasInteractiveLeasing: boolean): NavLink[] {
  return Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([, page]) => ({ label: page.name, href: page.path }));
}

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule A Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function Navigation() {
  const scheduleTourLink = useSlotGroup('navigation.schedule_tour_link', scheduleTourLinkDef);

  // Interactive Map page drops out unless the property is configured for the
  // ApartmentSync (Repli360 / RRAC) interactive-leasing widget — BOTH the
  // JavaScript ID and the Config ID must be present — or the editor has set
  // the Header's `interactive_map_show_if_exists` override to a non-empty
  // value. This mirrors Header.tsx so the two navs stay consistent; we read
  // the Header's existing override token rather than duplicating the control.
  const property = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = property?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = property?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(
    interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim()
  );
  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasInteractiveLeasing),
    [hasInteractiveLeasing],
  );

  return (
    <nav className="bg-[var(--vibe-color-surface)] border-b border-[var(--vibe-color-border)] sticky top-16 lg:top-[72px] z-40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12 overflow-x-auto">
          <div className="flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="rise-nav-darken-hover font-body text-sm text-[var(--vibe-color-text-muted)] transition-colors whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </div>

          <EditableSlotGroup groupPath="navigation.schedule_tour_link" definition={scheduleTourLinkDef}>
            <a
              href={scheduleTourLink.url || '/schedule-a-tour'}
              target={scheduleTourLink._target || '_self'}
              className="hidden md:inline-flex items-center px-4 py-1.5 text-xs font-medium tracking-wider uppercase bg-[var(--vibe-color-primary)] text-primary-foreground hover:bg-[var(--vibe-color-secondary)] hover:text-secondary-foreground transition-colors whitespace-nowrap"
            >
              {scheduleTourLink.label || 'Schedule A Tour'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
      {/* Nav links: animated underline on hover (no color shift). The
          underline rides the text baseline via `text-decoration-*` so it
          tracks the text correctly regardless of the link's padding —
          works equally well in this padding-less sub-nav, the padded
          header `DesktopNavLink`, the More-dropdown rows, and the mobile
          drawer rows. Header.tsx repeats the same class in its own
          <style> block — duplicate rules are harmless. */}
      <style>{`
        .rise-nav-darken-hover {
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .rise-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
      `}</style>
    </nav>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Navigation',
  displayName: 'Navigation',
  tokens: {},
  slotGroups: {
    'navigation.schedule_tour_link': scheduleTourLinkDef,
  },
};

(Navigation as any).contentSchema = contentSchema;

export default Navigation;
